import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from './AuthProvider.tsx';
import { UserRole } from '@/types/auth.ts';

interface ProtectedRouteProps {
  children: React.ReactNode;
  requireAuth?: boolean;
  /** Set to true for routes that EXTERNAL role users are allowed to access. */
  allowExternal?: boolean;
  /**
   * Per-user capability required to enter this route (checked via `can()`).
   * Used to gate the Stakeholder Portal with the `stakeholder_portal` capability
   * (SPEC-stakeholder-portal R5) — NOT a tenant-level FeatureGate. A signed-in
   * user WITHOUT the capability is redirected to /dashboard. The BACKEND is the
   * real security boundary (every portal endpoint is `require_permission`-gated);
   * this is the UI route gate so the page never mounts without the grant.
   */
  requireCapability?: string;
  /**
   * Restrict this route to super-admins (SPEC-stakeholder-portal R6 — the
   * internal cross-tenant coordination view). A signed-in non-super-admin is
   * bounced to /dashboard. The BACKEND is the real security boundary (every
   * coordination endpoint does a DIRECT is_super_admin check at the function
   * boundary); this is only the UI route gate so the page never mounts.
   */
  requireSuperAdmin?: boolean;
}

export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  children,
  requireAuth = true,
  allowExternal = false,
  requireCapability,
  requireSuperAdmin = false,
}) => {
  const { isAuthenticated, isLoading, user, can } = useAuth();
  const location = useLocation();

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-gray-900"></div>
      </div>
    );
  }

  if (requireAuth && !isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // EXTERNAL role users can only access explicitly allowed routes.
  // Redirect them to /my-views for any internal page they try to access.
  if (isAuthenticated && user?.role === UserRole.EXTERNAL && !allowExternal) {
    return <Navigate to="/my-views" replace />;
  }

  // Capability gate (e.g. the stakeholder_portal capability). A signed-in user
  // without the grant is bounced to the dashboard rather than shown the page.
  if (isAuthenticated && requireCapability && !(typeof can === 'function' && can(requireCapability))) {
    return <Navigate to="/dashboard" replace />;
  }

  // Super-admin gate (the internal coordination view, R6). A signed-in
  // non-super-admin is bounced to the dashboard rather than shown the page.
  if (isAuthenticated && requireSuperAdmin && !user?.is_super_admin) {
    return <Navigate to="/dashboard" replace />;
  }

  return <>{children}</>;
};